<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-truck-ramp-box me-2" style="color: #0ea5e9;"></i>{{ $t('Goods Receipt Report') }}
          </h5>
          <small class="text-muted">{{ $t('Purchases by supplier for handover to accountant') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-success btn-sm" @click="exportExcel" :disabled="loading || !rows.length">
          <i class="fa-solid fa-file-excel me-1"></i>{{ $t('Export Excel') }}
        </button>
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <select v-model="supplierFilter" class="form-select form-select-sm" style="min-width: 140px; max-width: 220px;" :disabled="loading">
            <option value="">{{ $t('All Suppliers') }}</option>
            <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Purchase') }}</div>
            <div class="fw-bold fs-5">{{ formatCurrency(summary.total_amount) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Before VAT') }}</div>
            <div class="fw-bold fs-5 text-success">{{ formatCurrency(summary.total_before_vat) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('VAT') }}</div>
            <div class="fw-bold fs-5 text-primary">{{ formatCurrency(summary.total_vat) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Receipts') }}</div>
            <div class="fw-bold fs-4">{{ summary.total_count || 0 }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Breakdown by Supplier -->
    <div v-if="summary.breakdown && summary.breakdown.length" class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-store me-2 text-info"></i>{{ $t('Purchase by Supplier') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Supplier') }}</th>
                <th class="text-end">{{ $t('Receipts') }}</th>
                <th class="text-end pe-3">{{ $t('Amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in summary.breakdown" :key="item.supplier">
                <td class="ps-3">{{ item.supplier }}</td>
                <td class="text-end">{{ item.count }}</td>
                <td class="text-end pe-3">{{ formatCurrency(item.amount) }}</td>
              </tr>
            </tbody>
            <tfoot class="table-light fw-bold">
              <tr>
                <td class="ps-3">{{ $t('Total') }}</td>
                <td class="text-end">{{ summary.total_count || 0 }}</td>
                <td class="text-end pe-3">{{ formatCurrency(summary.total_amount) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Detail Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-table me-2 text-secondary"></i>{{ $t('Goods Receipt Details') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3 text-end" style="width: 50px;">#</th>
                <th>{{ $t('Date') }}</th>
                <th>{{ $t('Document No.') }}</th>
                <th>{{ $t('Reference No.') }}</th>
                <th>{{ $t('Supplier') }}</th>
                <th>{{ $t('Branch') }}</th>
                <th class="text-end">{{ $t('Before VAT') }}</th>
                <th class="text-end">{{ $t('VAT') }}</th>
                <th class="text-end pe-3">{{ $t('Total') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!rows.length && !loading">
                <td colspan="9" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="row in rows" :key="row.seq">
                <td class="ps-3 text-end">{{ row.seq }}</td>
                <td class="text-nowrap">{{ row.date }}</td>
                <td class="fw-semibold">{{ row.document_no || '-' }}</td>
                <td>{{ row.ref_no || '-' }}</td>
                <td>{{ row.supplier || '-' }}</td>
                <td>{{ $t(row.branch || '-') }}</td>
                <td class="text-end">{{ formatCurrency(row.before_vat) }}</td>
                <td class="text-end">{{ formatCurrency(row.vat) }}</td>
                <td class="text-end pe-3 fw-semibold">{{ formatCurrency(row.total) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="rows.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3" colspan="6">{{ $t('Total') }}</td>
                <td class="text-end text-success">{{ formatCurrency(summary.total_before_vat) }}</td>
                <td class="text-end text-primary">{{ formatCurrency(summary.total_vat) }}</td>
                <td class="text-end pe-3">{{ formatCurrency(summary.total_amount) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { Reports } from '@/services/endpoint/reports.service';
import { exportRowsToXlsx, MONEY_FMT } from '@/utils/excelExport';
import reportMixin from './reportMixin';

export default {
  name: 'ReportGoodsReceipt',
  mixins: [reportMixin],
  data() {
    const start = moment().startOf('month').toDate();
    const end = moment().endOf('month').toDate();
    return {
      reportLabel: 'Goods Receipt Report',
      filters: {
        branch: 'all',
        date_range: { startDate: start, endDate: end },
        dateFrom: moment(start).format('YYYY-MM-DD'),
        dateTo: moment(end).format('YYYY-MM-DD'),
      },
      supplierFilter: '',
      suppliers: [],
      summary: {
        total_count: 0,
        total_before_vat: 0,
        total_vat: 0,
        total_amount: 0,
        breakdown: [],
      },
      rows: [],
    };
  },
  watch: {
    supplierFilter() {
      this.loadReport();
    },
  },
  methods: {
    buildFetchParams() {
      return {
        branch_id: this.filters.branch,
        start: this.formatDate(this.filters.date_range.startDate),
        end: this.formatDate(this.filters.date_range.endDate),
        supplier_id: this.supplierFilter || undefined,
      };
    },

    async fetchReport() {
      const res = await Reports.goodsReceiptReport(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.suppliers = filter.suppliers || [];
      this.summary = payload.summary || this.summary;
      this.rows = payload.rows || [];
    },

    toNum(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },

    exportExcel() {
      const from = this.formatDate(this.filters.date_range.startDate);
      const to = this.formatDate(this.filters.date_range.endDate);
      const columns = [
        { header: '#', key: 'seq', width: 6 },
        { header: this.$t('Date'), key: 'date', width: 14 },
        { header: this.$t('Document No.'), key: 'document_no', width: 20 },
        { header: this.$t('Reference No.'), key: 'ref_no', width: 16 },
        { header: this.$t('Supplier'), key: 'supplier', width: 24 },
        { header: this.$t('Branch'), value: r => this.$t(r.branch || ''), width: 18 },
        { header: this.$t('Before VAT'), value: r => this.toNum(r.before_vat), width: 14, numFmt: MONEY_FMT },
        { header: this.$t('VAT'), value: r => this.toNum(r.vat), width: 12, numFmt: MONEY_FMT },
        { header: this.$t('Total'), value: r => this.toNum(r.total), width: 16, numFmt: MONEY_FMT },
      ];
      const preRows = [
        [this.$t('Goods Receipt Report')],
        [this.selectedBranchLabel + '  |  ' + from + ' — ' + to],
        [this.$t('Receipts') + ': ' + (this.summary.total_count || 0), this.$t('Total Purchase') + ': ' + this.toNum(this.summary.total_amount)],
        [],
      ];
      const postRows = [
        [this.$t('Total'), '', '', '', '', '', this.toNum(this.summary.total_before_vat), this.toNum(this.summary.total_vat), this.toNum(this.summary.total_amount)],
      ];
      exportRowsToXlsx({
        filename: `goods-receipts-${from}-${to}.xlsx`,
        sheetName: 'Goods Receipts',
        columns,
        rows: this.rows,
        preRows,
        postRows,
      });
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        {
          columns: [
            { text: [{ text: this.$t('Total Purchase') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_amount), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Before VAT') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_before_vat), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('VAT') + '\n', style: 'kpiLabel' }, { text: this.formatCurrencyRaw(this.summary.total_vat), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Receipts') + '\n', style: 'kpiLabel' }, { text: String(this.summary.total_count || 0), style: 'kpiValue' }], width: '*' },
          ],
          margin: [0, 0, 0, 16],
        },
        { text: this.$t('Purchase by Supplier'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('Supplier'), style: 'tableHeader' },
                { text: this.$t('Receipts'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Amount'), style: 'tableHeader', alignment: 'right' },
              ],
              ...(this.summary.breakdown || []).map(item => [
                { text: item.supplier, style: 'tableCell' },
                { text: String(item.count), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(item.amount), style: 'tableCell', alignment: 'right' },
              ]),
              [
                { text: this.$t('Total'), style: 'tableHeader' },
                { text: String(this.summary.total_count || 0), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.summary.total_amount), style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 16],
        },
        { text: this.$t('Goods Receipt Details'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: [16, 'auto', 'auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: '#', style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Date'), style: 'tableHeader' },
                { text: this.$t('Document No.'), style: 'tableHeader' },
                { text: this.$t('Reference No.'), style: 'tableHeader' },
                { text: this.$t('Supplier'), style: 'tableHeader' },
                { text: this.$t('Branch'), style: 'tableHeader' },
                { text: this.$t('Before VAT'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('VAT'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Total'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.rows.map(r => [
                { text: String(r.seq), style: 'tableCell', alignment: 'right' },
                { text: r.date, style: 'tableCell' },
                { text: r.document_no || '-', style: 'tableCell' },
                { text: r.ref_no || '-', style: 'tableCell' },
                { text: r.supplier || '-', style: 'tableCell' },
                { text: this.$t(r.branch || '-'), style: 'tableCell' },
                { text: this.formatCurrencyRaw(r.before_vat), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(r.vat), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(r.total), style: 'tableCell', alignment: 'right' },
              ]),
              [
                { text: this.$t('Total'), style: 'tableHeader', colSpan: 6 }, {}, {}, {}, {}, {},
                { text: this.formatCurrencyRaw(this.summary.total_before_vat), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.summary.total_vat), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.summary.total_amount), style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];
      // 9 คอลัมน์ + ชื่อผู้ขายไทยยาว ๆ ล้น A4 แนวตั้ง (วัดได้ 586pt จากที่มี 555pt)
      // แนวนอนพอ เพราะคอลัมน์ผู้ขายเป็น '*' ซึ่งยืดหดรับส่วนที่เหลือให้เอง
      return { pageOrientation: 'landscape', content };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
